'use client';

import { type ReactNode, forwardRef, isValidElement, useEffect, useRef, useState } from 'react';

import { useCallbackRef } from '../../hooks/use-callback-ref';
import type { ToastPropsInterface } from '../toast/types/toast.types';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import {
  TOAST_ANNOUNCEMENT_BURST_WINDOW_MS,
  TOAST_ANNOUNCEMENT_SET_DELAY_MS,
} from './constants/live-announcer.constants';
import type { ToastProviderLiveAnnouncerPropsInterface } from './types/toast-provider-live-announcer.types';
import type { ToastObjectInterface } from './types/use-toast-queue.types';

const getAnnouncementTextFromNode = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getAnnouncementTextFromNode).join(' ').trim();
  }

  if (isValidElement(node)) {
    return getAnnouncementTextFromNode(node.props.children);
  }

  return '';
};

const getNotificationPrefix = (tone: ToastPropsInterface['tone'] = 'info') => {
  if (tone === 'critical') {
    return 'Critical notification';
  }

  if (tone === 'warning') {
    return 'Warning notification';
  }

  return 'Notification';
};

const getToastAnnouncementParts = (toast?: ToastObjectInterface) => {
  const tone = toast?.node?.props?.tone ?? 'info';
  const notificationPrefix = getNotificationPrefix(tone);
  const content = getAnnouncementTextFromNode(toast?.node?.props?.children)
    .trim()
    .replace(/\s+/g, ' ');

  return { content, notificationPrefix };
};

const getToastAnnouncementText = (toast?: ToastObjectInterface) => {
  const { content, notificationPrefix } = getToastAnnouncementParts(toast);

  return content ? `${notificationPrefix}: ${content}` : notificationPrefix;
};

const getToastDismissedAnnouncementText = (toast?: ToastObjectInterface) => {
  const { content, notificationPrefix } = getToastAnnouncementParts(toast);

  return content
    ? `${notificationPrefix} dismissed: ${content}`
    : `${notificationPrefix} dismissed`;
};

const getCombinedToastAnnouncementText = (toasts: Array<ToastObjectInterface>) => {
  const toastCount = toasts.length;
  const joinedAnnouncements = toasts
    .map((toast) => {
      const { content } = getToastAnnouncementParts(toast);
      const tone = toast?.node?.props?.tone ?? 'info';

      if (!content) {
        return '';
      }

      if (tone === 'critical') {
        return `Critical: ${content}`;
      }

      if (tone === 'warning') {
        return `Warning: ${content}`;
      }

      return content;
    })
    .filter(Boolean)
    .join('. ');

  const notificationLabel = toastCount === 1 ? 'notification' : 'notifications';
  return joinedAnnouncements
    ? `${toastCount} ${notificationLabel}: ${joinedAnnouncements}`
    : `${toastCount} ${notificationLabel}`;
};

export const ToastProviderLiveAnnouncer = forwardRef<
  HTMLSpanElement,
  ToastProviderLiveAnnouncerPropsInterface
>((props, forwardedRef) => {
  const { activeIds, dismiss, toasts, ...rest } = props;
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const lastQueuedToastIdRef = useRef(-1);
  const lastAnnouncementRef = useRef('');
  const announcementSetTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announcementBurstTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingBurstToastsRef = useRef<Array<ToastObjectInterface>>([]);

  const announce = useCallbackRef((nextAnnouncement: string) => {
    setLiveAnnouncement('');

    if (announcementSetTimeoutIdRef.current) {
      clearTimeout(announcementSetTimeoutIdRef.current);
    }

    announcementSetTimeoutIdRef.current = setTimeout(() => {
      const normalizedAnnouncement =
        lastAnnouncementRef.current === nextAnnouncement
          ? `${nextAnnouncement}\u00A0`
          : nextAnnouncement;

      setLiveAnnouncement(normalizedAnnouncement);
      lastAnnouncementRef.current = normalizedAnnouncement;
      announcementSetTimeoutIdRef.current = null;
    }, TOAST_ANNOUNCEMENT_SET_DELAY_MS);
  });

  const handleKeyDown = useCallbackRef((event: KeyboardEvent) => {
    if (event.key !== 'Escape' || activeIds.length === 0) {
      return;
    }

    const escapeDismissToastId = activeIds.at(0);

    if (typeof escapeDismissToastId === 'number') {
      const dismissedToast = toasts.find((toast) => toast.id === escapeDismissToastId);
      event.stopPropagation();
      dismiss(escapeDismissToastId);
      announce(getToastDismissedAnnouncementText(dismissedToast));
    }
  });

  const flushBurstAnnouncements = useCallbackRef(() => {
    announcementBurstTimeoutIdRef.current = null;
    const burstToasts = pendingBurstToastsRef.current;

    if (burstToasts.length === 0) {
      return;
    }

    pendingBurstToastsRef.current = [];

    if (burstToasts.length === 1) {
      announce(getToastAnnouncementText(burstToasts[0]));
      return;
    }

    announce(getCombinedToastAnnouncementText(burstToasts));
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const nextToastsToAnnounce = toasts.filter((toast) => toast.id > lastQueuedToastIdRef.current);

    if (nextToastsToAnnounce.length === 0) {
      return;
    }

    pendingBurstToastsRef.current.push(...nextToastsToAnnounce);
    lastQueuedToastIdRef.current = nextToastsToAnnounce.at(-1)?.id ?? lastQueuedToastIdRef.current;

    if (announcementBurstTimeoutIdRef.current) {
      clearTimeout(announcementBurstTimeoutIdRef.current);
    }

    announcementBurstTimeoutIdRef.current = setTimeout(
      flushBurstAnnouncements,
      TOAST_ANNOUNCEMENT_BURST_WINDOW_MS,
    );
  }, [flushBurstAnnouncements, toasts]);

  useEffect(() => {
    return () => {
      if (announcementBurstTimeoutIdRef.current) {
        clearTimeout(announcementBurstTimeoutIdRef.current);
      }

      if (announcementSetTimeoutIdRef.current) {
        clearTimeout(announcementSetTimeoutIdRef.current);
      }
    };
  }, []);

  return (
    <VisuallyHidden
      ref={forwardedRef}
      aria-atomic
      aria-live="assertive"
      data-c11n-component="ToastProvider.LiveAnnouncer"
      data-c11n-version={__C11N_VERSION__}
      {...rest}
    >
      {liveAnnouncement}
    </VisuallyHidden>
  );
});

ToastProviderLiveAnnouncer.displayName = 'ToastProvider.LiveAnnouncer';
